import React, { useMemo } from 'react';
import { View, TouchableOpacity, Platform } from 'react-native';
import { Text } from '@/components/gluestack/text';
import { useTranslation } from 'react-i18next';
import { BillingService } from '@/services/domain/BillingService';
import { GiftCertificatesView, GiftCertificateItem } from '@/models/Billing';
import dayjs from '@/i18n/dayjs-config';
import {
  GiftIcon,
  Copy01Icon,
  Link06Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import { VStack } from '@/components/gluestack/vstack';
import { Heading } from '@/components/gluestack/heading';
import { Card } from '@/components/gluestack/card';
import { useRegisterRightPanelSlot } from '@/components/layout/RightPanelSlotContext';
import { useIsRightPanelVisible } from '@/components/layout/LargeScreenRightPanel';
import { useToastMessages } from '@/hooks/useToastMessages';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import Clipboard from '@react-native-clipboard/clipboard';
import { router } from 'expo-router';
import { useAuth } from '@/components/providers/AuthProvider';
import { DataList } from '@/components/ui/DataList';
import { getNextPageSkipOrCursor, buildPaginationParams } from '@/hooks/usePaginationUtils';
import { RedeemGiftCertificateForm } from '@/components/domain/account/RedeemGiftCertificateForm';

export default function GiftCertificatesScreen() {
  const { t } = useTranslation();
  const { currentCommunity } = useAuth();
  const { showSuccessToast } = useToastMessages();

  const isWeb = Platform.OS === 'web';
  const isRightPanelVisible = useIsRightPanelVisible();
  const rightPanelContent = useMemo(
    () =>
      isWeb ? (
        <View>
          <Text className="mb-4 text-sm font-bold uppercase text-tertiary-500">
            {t('account.certificates.redeem.title')}
          </Text>
          <RedeemGiftCertificateForm fullWidth />
        </View>
      ) : null,
    [isWeb, t]
  );
  useRegisterRightPanelSlot(rightPanelContent, 'wide');

  const handleCopyCode = (code: string) => {
    Clipboard.setString(code);
    showSuccessToast(t('account.certificates.codeCopied'), undefined, 'bottom');
  };

  return (
    <View className="flex-1">
      <DataList<GiftCertificatesView, GiftCertificateItem>
        title={t('account.certificates.title')}
        queryKey={['billing', 'gift-certificates', currentCommunity]}
        queryFn={async ({ pageParam }) =>
          await BillingService.getGiftCertificates(buildPaginationParams(pageParam, 25))
        }
        getNextPageParam={getNextPageSkipOrCursor}
        getPageItems={(page) => page.Items}
        getItemKey={(item) => String(item.Id)}
        emptyMessage={t('account.certificates.empty')}
        emptyIcon={GiftIcon}
        headerContent={
          isWeb && !isRightPanelVisible ? (
            <VStack space="2xl">
              <VStack space="sm">
                <Heading size="md" className="ml-1 text-typography-700">
                  {t('account.certificates.redeem.title')}
                </Heading>
                <Card size="md" variant="outline" className="border-outline-100 bg-background-0">
                  <RedeemGiftCertificateForm />
                </Card>
              </VStack>
              <Heading size="md" className="ml-1 text-typography-700">
                {t('account.certificates.history')}
              </Heading>
            </VStack>
          ) : undefined
        }
        columns={[
          {
            width: 'w-32',
            renderHeader: () => (
              <Text className="text-left font-extrabold leading-6 text-typography-800">
                {t('account.certificates.header.date')}
              </Text>
            ),
            renderContent: (item) => (
              <>
                <Text size="sm" bold className="leading-5 text-typography-800">
                  {dayjs(
                    item.Kind === 'Redeemed'
                      ? (item.DateRedeemed ?? item.DatePurchased)
                      : item.DatePurchased
                  ).format('YYYY-MM-DD')}
                </Text>
                <View className="mt-1">
                  <Badge action="success" variant="solid" size="sm" className="w-auto self-start">
                    <BadgeText size="md">
                      <BadgeText size="md" className="font-bold">
                        {item.Days}
                      </BadgeText>{' '}
                      {t('account.transactions.days', { count: item.Days })}
                    </BadgeText>
                  </Badge>
                </View>
              </>
            ),
          },
          {
            width: 'flex-1',
            renderHeader: () => (
              <Text className="text-left font-extrabold leading-6 text-typography-800">
                {t('account.certificates.header.details')}
              </Text>
            ),
            renderContent: (item) => {
              const isRedeemed = !!item.DateRedeemed;
              // A certificate this member redeemed from someone else: the date column already shows when
              const isRedeemedByMe = item.Kind === 'Redeemed';

              return (
                <VStack className="gap-1">
                  <Text className="font-semibold leading-5 text-typography-800">
                    {t(
                      isRedeemedByMe
                        ? 'account.certificates.redeemed'
                        : 'account.certificates.purchase'
                    )}
                  </Text>
                  <View className="mt-0.5 flex-row flex-wrap gap-1">
                    {isRedeemed ? (
                      <Badge action="muted" variant="solid" size="sm">
                        <BadgeText size="md" className="font-mono">
                          ***{item.Code.slice(-5)}
                        </BadgeText>
                      </Badge>
                    ) : (
                      <TouchableOpacity onPress={() => handleCopyCode(item.Code)} activeOpacity={0.7}>
                        <Badge action="muted" variant="solid" size="sm" className="flex-row items-center gap-1.5">
                          <HugeIconsWrapper icon={Copy01Icon} size={16} className="text-typography-800" />
                          <BadgeText size="md" className="font-mono">
                            {item.Code}
                          </BadgeText>
                        </Badge>
                      </TouchableOpacity>
                    )}
                    {isRedeemed && !isRedeemedByMe && item.DateRedeemed && (
                      <Badge action="info" variant="solid" size="sm">
                        <BadgeText size="md">
                          {t('account.certificates.redeemedOn', {
                            date: dayjs(item.DateRedeemed).format('YYYY-MM-DD'),
                          })}
                        </BadgeText>
                      </Badge>
                    )}
                    {isRedeemed && !isRedeemedByMe && item.RedeemedBy && (
                      <TouchableOpacity onPress={() => router.push(`/@/${item.RedeemedBy}`)} activeOpacity={0.7}>
                        <Badge action="info" variant="solid" size="sm" className="flex-row items-center gap-1.5">
                          <HugeIconsWrapper icon={Link06Icon} size={16} className="text-info-600" />
                          <BadgeText size="md">{item.RedeemedBy}</BadgeText>
                        </Badge>
                      </TouchableOpacity>
                    )}
                  </View>
                </VStack>
              );
            },
          },
        ]}
      />
    </View>
  );
}

